/**
 * Spark Explore admin API service.
 *
 * Wraps the platform-staff Explore endpoints (/admin/explore/*):
 * - read-only trend browsing across every stored trend (all topics, all
 *   freshness states — not just the public feed), server-side paginated;
 * - collection execution history/status;
 * - the platform_admin-only manual trigger, which asks the backend to invoke
 *   the same collector Lambdas the EventBridge schedule uses.
 *
 * Nothing here talks to Google or X directly — provider credentials stay on
 * the backend, and opening the dashboard never starts a collection.
 */

import { api } from "@/lib/api";

/**
 * Collection providers, as only the admin console sees them.
 *
 * Three of them answer for Google — SerpAPI (`google`), Serper and
 * DataForSEO — and two for X: the official API (`x`) and TwitterAPI.io.
 * Each set is grouped under its platform everywhere in this console
 * (`ExploreSourceGroup`). Users never see either split: every /explore route
 * reports `google` for the first three and `x` for the other two.
 */
export type ExploreSource =
  | "google"
  | "serper"
  | "dataforseo"
  | "x"
  | "twitterapi";
/** What users and the public API call a source. */
export type ExplorePublicSource = "google" | "x";
export type ExploreExecutionScope = "all" | ExploreSource;

export const EXPLORE_SOURCES: ExploreSource[] = [
  "google",
  "serper",
  "dataforseo",
  "x",
  "twitterapi",
];

/** Vendor names, read under their group's heading. */
export const EXPLORE_SOURCE_LABELS: Record<ExploreSource, string> = {
  google: "SerpAPI",
  serper: "Serper",
  dataforseo: "DataForSEO",
  x: "X API",
  twitterapi: "TwitterAPI.io",
};

export const EXPLORE_PUBLIC_SOURCE_LABELS: Record<ExplorePublicSource, string> =
  {
    google: "Google",
    x: "X",
  };

export function isExploreSource(value: unknown): value is ExploreSource {
  return (
    typeof value === "string" && (EXPLORE_SOURCES as string[]).includes(value)
  );
}

/** A provider's full name where it appears without its group heading. */
export function exploreSourceFullLabel(source: ExploreSource): string {
  const group = source === "x" || source === "twitterapi" ? "X" : "Google";
  return `${group} · ${EXPLORE_SOURCE_LABELS[source]}`;
}

export interface ExploreSourceProvider {
  source: ExploreSource;
  label: string;
  /** False while this provider's scheduled collection is switched off. */
  enabled: boolean;
  /** False where the collector Lambda is not deployed (preview stages). */
  available: boolean;
  configuredAt?: string;
  configuredBy?: string;
  state: ExplorePipelineState | null;
}

export interface ExploreSourceGroup {
  source: ExplorePublicSource;
  label: string;
  providers: ExploreSourceProvider[];
}

export interface ExploreSourceConfig {
  source: ExploreSource;
  enabled: boolean;
  updatedAt?: string;
  updatedBy?: string;
}
export type ExploreFreshnessFilter = "all" | "fresh" | "expired";
export type ExploreTrendSort = "updated" | "popularity";
export type ExploreRecordType = "query" | "event" | "unclassified";

export interface ExploreSourceMetadata {
  classificationMethod?: string;
  fetchedAt?: string;
  popularity?: number;
  sourceId?: string;
  sourceUrl?: string;
  signalType?: string;
  queryCount?: number;
  eventCount?: number;
  unclassifiedCount?: number;
}

export interface AdminExploreTrend {
  id: string;
  topicId: string;
  topic: string;
  subtopicId: string;
  subtopic: string;
  title: string;
  normalizedName?: string;
  snippet?: string;
  url?: string;
  popularity: number;
  sources: ExploreSource[];
  sourceMetadata?: Partial<Record<ExploreSource, ExploreSourceMetadata>>;
  fetchedAt: string;
  expiresAt: string;
  firstSeenAt?: string;
  /** "fresh" until expiresAt passes; expired trends stay stored for admins. */
  freshness: "fresh" | "expired";
  /** Fresh trends are candidates for users' Explore feeds (per-user topics and caps still apply). */
  feedEligible: boolean;
  counts: { queries: number; events: number; unclassified: number };
}

export interface ExploreTrendListResponse {
  items: AdminExploreTrend[];
  cursor?: string;
  hasMore: boolean;
  index?: string;
}

export interface ExploreTrendRecord {
  id: string;
  trendId: string;
  type: ExploreRecordType;
  text: string;
  source: ExploreSource;
  sourceId?: string;
  sourceUrl?: string;
  classificationMethod?: string;
  fetchedAt: string;
  expiresAt: string;
  expired: boolean;
}

export interface ExploreRecordPage {
  items: ExploreTrendRecord[];
  cursor?: string;
  hasMore: boolean;
}

export interface ExploreTrendDetail {
  trend: AdminExploreTrend;
  queries: ExploreRecordPage;
  events: ExploreRecordPage;
  unclassified: ExploreRecordPage;
}

export type ExploreExecutionStatus =
  | "accepted"
  | "running"
  | "succeeded"
  | "partial"
  | "failed"
  | "skipped"
  | "cancelled";

export type ExplorePipelineStatus =
  | "queued"
  | "running"
  | "retrying"
  | "succeeded"
  | "partial"
  | "failed"
  | "skipped"
  | "cancelled";

export interface ExploreExecutionCounts {
  trendsCreated: number;
  trendsUpdated: number;
  queriesWritten: number;
  eventsWritten: number;
  unclassifiedWritten: number;
}

export interface ExplorePipelineRun extends Partial<ExploreExecutionCounts> {
  status: ExplorePipelineStatus;
  startedAt?: string;
  completedAt?: string;
  leaseExpiresAt?: string;
  attempts?: number;
  topicsProcessed?: number;
  /** Topics that produced at least one candidate; the run is judged on the rate. */
  topicsSucceeded?: number;
  apiRequestsMade?: number;
  failures?: string[];
  warnings?: string[];
}

export interface ExploreExecution {
  id: string;
  trigger: "scheduled" | "manual";
  scope: ExploreExecutionScope;
  requestedBy?: string;
  requestedAt: string;
  startedAt?: string;
  completedAt?: string;
  status: ExploreExecutionStatus;
  active: boolean;
  /** Set once "Stop collection" has been pressed; stays set on the finished run. */
  cancelRequested?: boolean;
  cancelRequestedAt?: string;
  cancelRequestedBy?: string;
  pipelines: Partial<Record<ExploreSource, ExplorePipelineRun>>;
  totals: ExploreExecutionCounts;
  updatedAt?: string;
}

export interface ExploreExecutionListResponse {
  items: ExploreExecution[];
  cursor?: string;
  hasMore: boolean;
}

export interface ExplorePipelineState {
  status?: string;
  lastAttemptAt?: string;
  lastSuccessAt?: string;
  topicsProcessed?: number;
  /** Topics that produced at least one candidate. Absent on older states. */
  topicsSucceeded?: number;
  apiRequestsMade?: number;
  failures?: string[];
  opportunitiesSaved?: number;
  /**
   * List-price estimate for the last run, and the same per trend it stored.
   * Request counts are not comparable between providers, which charge
   * differently; these are what a choice of primary provider rests on.
   */
  estimatedCostUsd?: number;
  costPerTrendUsd?: number;
}

export interface ExploreAdminOverview {
  topics: {
    id: string;
    name: string;
    subtopics: { id: string; name: string }[];
  }[];
  pipelines: Record<ExploreSource, ExplorePipelineState | null>;
  /** Providers grouped under the public source they collect for. */
  sourceGroups: ExploreSourceGroup[];
  activeExecutions: ExploreExecution[];
  collectionAvailable: Record<ExploreSource, boolean>;
  /** True only for platform_admin — analysts can view but not trigger. */
  canTrigger: boolean;
}

export interface ExploreTrendListParams {
  search?: string;
  topicId?: string;
  subtopicId?: string;
  source?: ExploreSource;
  freshness?: ExploreFreshnessFilter;
  sort?: ExploreTrendSort;
  order?: "asc" | "desc";
  limit?: number;
  cursor?: string;
}

function toQuery(params: object | undefined): string {
  const qs = new URLSearchParams();
  for (const [key, value] of Object.entries(params ?? {})) {
    if (value !== undefined && value !== null && value !== "") {
      qs.set(key, String(value));
    }
  }
  const str = qs.toString();
  return str ? `?${str}` : "";
}

export const exploreAdminApi = {
  getOverview(): Promise<ExploreAdminOverview> {
    return api.get("/admin/explore/overview");
  },

  listTrends(
    params?: ExploreTrendListParams
  ): Promise<ExploreTrendListResponse> {
    return api.get(`/admin/explore/trends${toQuery(params)}`);
  },

  getTrend(id: string): Promise<ExploreTrendDetail> {
    return api.get(`/admin/explore/trends/${encodeURIComponent(id)}`);
  },

  getTrendRecords(
    id: string,
    params: {
      type: ExploreRecordType;
      source?: ExploreSource;
      limit?: number;
      cursor?: string;
    }
  ): Promise<ExploreRecordPage> {
    return api.get(
      `/admin/explore/trends/${encodeURIComponent(id)}${toQuery(params)}`
    );
  },

  listExecutions(params?: {
    limit?: number;
    cursor?: string;
  }): Promise<ExploreExecutionListResponse> {
    return api.get(`/admin/explore/executions${toQuery(params)}`);
  },

  getExecution(id: string): Promise<ExploreExecution> {
    return api.get(`/admin/explore/executions/${encodeURIComponent(id)}`);
  },

  /** platform_admin only. 409 when a conflicting run is active; 503 where collectors are not deployed. */
  triggerExecution(scope: ExploreExecutionScope): Promise<ExploreExecution> {
    return api.post("/admin/explore/executions", { scope });
  },

  /**
   * platform_admin only. Asks a run in progress to stop at its next
   * checkpoint. 202 means the request was recorded, not that the run has
   * stopped — poll the execution for its `cancelled` status. 409 when the
   * run already finished.
   */
  cancelExecution(id: string): Promise<ExploreExecution> {
    return api.post(
      `/admin/explore/executions/${encodeURIComponent(id)}/cancel`,
      {}
    );
  },

  /**
   * platform_admin only. Switches one provider's SCHEDULED collection on or
   * off. It does not stop a run already in flight, and a disabled provider
   * can still be run by hand from this console.
   */
  setSourceEnabled(
    source: ExploreSource,
    enabled: boolean
  ): Promise<ExploreSourceConfig> {
    return api.put(`/admin/explore/sources/${encodeURIComponent(source)}`, {
      enabled,
    });
  },
};
